import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  CashSpecialIcon,
  Layout,
  PageTitle,
  RoundedCheckIcon,
} from "lola-framework-ui-test";
import type { AxiosError } from "axios";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import { formatLabeledAmount } from "../utils";
import { COUNTRY_CURRENCY } from "../hooks/useExchangeRate";
import {
  createOperation,
  getQuote,
  getAllPaymentMethods,
  isDeadLinkError,
} from "../services/api";
import type {
  ICreateOperationRequest,
  IOperationResponse,
} from "../services/api";
import {
  FUNDING_METHOD_CATEGORY,
  categoryFromFundingMethod,
} from "../utils/catalog";
import {
  createWithFreshQuote,
  waitForPersistHydration,
} from "../utils/quoteCommit";
import { getEvents, safeCapture } from "../analytics";
import {
  clearFirstSendWarningCache,
  wasFirstSendWarningShown,
} from "../hooks/useFirstSendWarning";
import { clearPhoneSkip } from "../utils/phoneGate";
import {
  forgetAuthorizeCvv,
  peekAuthorizeCvv,
} from "../utils/fresh-cvv-alias";
import type { FundingMethod } from "../utils/catalog";
import type { IUserData } from "../shared/session/interfaces/session";
import { useRequestPayableGuard } from "../hooks/useRequestPayableGuard";
import { useComposeStore } from "../store/useComposeStore";
import { isPayoutEditable } from "../utils/payoutAnchor";
import { PayoutMethodType } from "../interfaces/payout";
import { resolvePayoutPaymentMethodId } from "../utils/registerPayoutInstrument";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";

/**
 * Dead-link errors (LINK_NOT_FOUND / 404, expired, revoked) are handled
 * globally by the dead-link event handler, so we must not override them here.
 */
const isSessionExpiredError = (err: unknown): boolean => {
  const axiosError = err as AxiosError<{ error?: { code?: string } }>;
  const errorCode = axiosError?.response?.data?.error?.code;
  return (
    isDeadLinkError(err) ||
    errorCode === "LINK_NOT_FOUND" ||
    axiosError?.response?.status === 404
  );
};

const isRequestAlreadyFulfilledError = (err: unknown): boolean => {
  const axiosError = err as AxiosError<{ error?: { code?: string } }>;
  return (
    axiosError?.response?.status === 409 &&
    axiosError?.response?.data?.error?.code === "REQUEST_ALREADY_FULFILLED"
  );
};

/**
 * Business-case recipe of the fund-request flow: the recipient is the
 * REQUESTER of the money (the person who minted the claim link), so the
 * recipient party is built from the claim's `userData`. Prefers the
 * structured `name` when present; degrades to a positional split of
 * `fullName`. Returns `undefined` when the claim carries no usable name —
 * the operation is then created without a recipient party (the gap surfaces
 * via the backend's completeness event).
 */
/** Claim links use partner `secondName`; operations API expects `middleName`. */
const toCanonicalRecipientName = (
  name: NonNullable<IUserData["name"]> & {
    secondName?: string;
    middleName?: string;
    secondLastName?: string;
  },
): NonNullable<ICreateOperationRequest["recipient"]>["name"] => ({
  firstName: name.firstName,
  lastName: name.lastName,
  ...(name.middleName ?? name.secondName
    ? { middleName: name.middleName ?? name.secondName }
    : {}),
  ...(name.secondLastName ? { secondLastName: name.secondLastName } : {}),
});

const buildRecipientFromClaim = (
  userData: IUserData | undefined,
): ICreateOperationRequest["recipient"] => {
  if (userData?.name?.firstName && userData?.name?.lastName) {
    return { name: toCanonicalRecipientName(userData.name) };
  }
  const tokens = userData?.fullName?.trim().split(/\s+/) ?? [];
  if (tokens.length >= 2) {
    return {
      name: {
        firstName: tokens[0],
        lastName: tokens.slice(1).join(" "),
      },
    };
  }
  return undefined;
};

/**
 * Module-level guard preventing duplicate operation creation across
 * unmount/remount cycles (StrictMode, routing re-mounts). Only ONE
 * executeOperation can be in flight; it resets when the operation settles.
 */
let isOperationInFlight = false;

const MIN_TRANSFER_VISIBLE_MS = 2000;

const holdMinimumVisible = (visibleSince: number) => {
  const remaining = MIN_TRANSFER_VISIBLE_MS - (Date.now() - visibleSince);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

/**
 * Commit screen ("creación de la comanda").
 *
 * Reached either straight from the transfer screen (no CIP needed) or from the
 * CIP screen once verification succeeds. On mount it creates the money-transfer
 * operation from the signed quote stored in global state, refreshing the
 * quote first if it has expired. Cash deposit then goes to the how-to
 * interstitial; card (and other instrument rails) show a terminal success view.
 */
export const SendingMoneyPage = memo(() => (
  <RequireActiveClerkSession resume={{ kind: "transfer" }} requireOccupant>
    <SendingMoneyPageContent />
  </RequireActiveClerkSession>
));

const SendingMoneyPageContent = memo(() => {
  useRequestPayableGuard();
  const { t } = useTranslation();
  const { theme, quoteState, setQuoteState } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, dropzoneColors, backgroundIcon } =
    colorConfig;

  const [operation, setOperation] = useState<IOperationResponse | null>(null);
  const visibleSinceRef = useRef(Date.now());

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("committing"));

    if (isOperationInFlight) {
      console.log("[SendingMoneyPage] Skipping - operation already in flight");
      return;
    }
    isOperationInFlight = true;

    const resolveRoute = () => {
      const config = session.data?.config;
      const corridor = config?.defaultCorridor || config?.corridors?.[0];
      const sourceCountry = corridor?.originCountryCode || "US";
      const destinationCountry = corridor?.destinationCountryCode || "MX";
      const sourceCurrency =
        config?.sourceCurrency || COUNTRY_CURRENCY[sourceCountry] || "USD";
      return { sourceCountry, destinationCountry, sourceCurrency };
    };

    const executeOperation = async () => {
      try {
        await waitForPersistHydration(useStore.persist);
        const capturedQuoteState = useStore.getState().quoteState;
        const linkId = session.data?.linkId;
        const signedQuote = capturedQuoteState?.signedQuote;

        const fundingMethod = capturedQuoteState?.fundingMethod as
          | FundingMethod
          | undefined;
        const category = fundingMethod
          ? FUNDING_METHOD_CATEGORY[fundingMethod]
          : undefined;

        const fundingSkipsInstrument = category === "CASH";

        let fundingPaymentMethodId: string | undefined;

        const {
          paymentState,
          preparedFundingPaymentMethodId,
          preparedPayoutPaymentMethodId,
        } = useStore.getState();
        const config = session.data?.config;
        const payoutEditable = isPayoutEditable(config);
        const composeSelection = useComposeStore.getState().selection;
        const corridor = config?.defaultCorridor || config?.corridors?.[0];
        const destinationCountry = corridor?.destinationCountryCode || "MX";

        if (!fundingSkipsInstrument) {
          if (preparedFundingPaymentMethodId) {
            fundingPaymentMethodId = preparedFundingPaymentMethodId;
          } else if (paymentState.payment?.cardId) {
            fundingPaymentMethodId = paymentState.payment.cardId;
          } else if (linkId) {
            try {
              const paymentMethods = await getAllPaymentMethods(linkId);
              if (category === "ACCOUNT") {
                const ba =
                  paymentMethods.bankAccounts.find(
                    (b) => b.isDefault && b.enabled
                  ) || paymentMethods.bankAccounts.find((b) => b.enabled);
                fundingPaymentMethodId = ba?.paymentMethodId;
              }
            } catch (pmErr) {
              console.error(
                "[SendingMoneyPage] Failed to fetch payment methods:",
                pmErr
              );
            }
          }
        }

        if (!linkId) {
          throw new Error("No linkId found in session");
        }
        if (!signedQuote || !capturedQuoteState) {
          throw new Error("No signed quote available");
        }
        if (!fundingSkipsInstrument && !fundingPaymentMethodId) {
          throw new Error("No funding payment method selected");
        }

        const recipient = buildRecipientFromClaim(session.data?.userData);

        let payoutPaymentMethodId = preparedPayoutPaymentMethodId ?? undefined;
        // Only a destination the payer chose gets an instrument registered.
        // The requester's own payout arrives masked in the claim; create
        // resolves it from the link, server-side.
        if (
          payoutEditable &&
          composeSelection?.origin === "user" &&
          !payoutPaymentMethodId &&
          (composeSelection.method === PayoutMethodType.PUSH_TO_CARD ||
            composeSelection.method === PayoutMethodType.BANK_ACCOUNT)
        ) {
          const cardholderName = session.data?.userData?.fullName;
          payoutPaymentMethodId = await resolvePayoutPaymentMethodId(
            linkId,
            composeSelection.method,
            composeSelection.payout,
            destinationCountry,
            cardholderName,
          );
        }

        const fundingCvvToken =
          category === "CARD" && fundingPaymentMethodId
            ? peekAuthorizeCvv(fundingPaymentMethodId)
            : undefined;

        const { result: created, quote: usedQuote } =
          await createWithFreshQuote({
            quote: capturedQuoteState,
            route: resolveRoute(),
            getQuote,
            create: (freshSignedQuote) =>
              createOperation(linkId, {
                signedQuote: freshSignedQuote,
                ...(fundingPaymentMethodId
                  ? { fundingPaymentMethodId }
                  : {}),
                ...(fundingCvvToken ? { fundingCvvToken } : {}),
                ...(payoutPaymentMethodId ? { payoutPaymentMethodId } : {}),
                ...(recipient ? { recipient } : {}),
              }),
          });
        setQuoteState(usedQuote);
        if (fundingCvvToken && fundingPaymentMethodId) {
          forgetAuthorizeCvv(fundingPaymentMethodId);
        }
        safeCapture(getEvents().OPERATION_CREATED, {
          success: true,
          first_send_warning_shown: wasFirstSendWarningShown(),
          operation_id: created.operationId,
          quote_id: created.quoteId,
          status: created.status,
          send_amount: created.sendAmount,
          receive_amount: created.receiveAmount,
          exchange_rate: created.exchangeRate,
          total_cost: created.totalCost,
          funding_payment_method_id: fundingPaymentMethodId,
          funding_method: fundingMethod,
          payout_method: usedQuote.payoutMethod,
          capability_key: usedQuote.capabilityKey,
        });
        clearFirstSendWarningCache();
        if (session.data?.linkId) clearPhoneSkip(session.data.linkId);
        await holdMinimumVisible(visibleSinceRef.current);
        if (fundingSkipsInstrument || created.fundingMethod === "CASH_DEPOSIT") {
          redirectTo(APP_ROUTES.HOW_TO_DEPOSIT_CASH_PAGE, { replace: true });
          return;
        }
        setOperation(created);
      } catch (err) {
        const axiosError = err as AxiosError<{
          error?: { code?: string; message?: string };
        }>;
        console.error("[SendingMoneyPage] Failed to create operation:", {
          message: axiosError?.message,
          status: axiosError?.response?.status,
          serverErrorCode: axiosError?.response?.data?.error?.code,
          serverErrorMessage: axiosError?.response?.data?.error?.message,
        });

        if (isSessionExpiredError(err)) {
          return;
        }

        safeCapture(getEvents().OPERATION_CREATED, {
          success: false,
          first_send_warning_shown: wasFirstSendWarningShown(),
          error: err instanceof Error ? err.message : "Create operation failed",
          error_code: "OPERATION_CREATE_FAILED",
        });
        clearFirstSendWarningCache();
        if (session.data?.linkId) clearPhoneSkip(session.data.linkId);

        if (isRequestAlreadyFulfilledError(err)) {
          redirectTo(APP_ROUTES.REQUEST_ALREADY_FULFILLED_PAGE, {
            replace: true,
          });
          return;
        }

        redirectTo(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
      } finally {
        isOperationInFlight = false;
      }
    };

    executeOperation();
    // No cleanup: the operation must run to completion even if React
    // unmounts/remounts this component. The module-level guard avoids dupes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayAmount = operation?.sendAmount ?? quoteState?.sendAmount ?? 0;
  const displayCurrency =
    operation?.sourceCurrency ?? quoteState?.sourceCurrency ?? "USD";

  // Cash funding never "transfers" here: the commit only creates the operation
  // and the deposit happens later at the store, so the spinner announces the
  // creation instead.
  const isCashFunding =
    categoryFromFundingMethod(
      operation?.fundingMethod ?? quoteState?.fundingMethod,
    ) === "CASH";

  // "Done" returns to the home (operations list) where the freshly-created
  // operation is now visible.
  const handleDone = () => {
    redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
  };

  if (operation) {
    return (
      <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
        <Layout.Content className="landing-section flow-hero-section">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark
              dropzoneColors={dropzoneColors}
              backgroundIcon={backgroundIcon}
            >
              <RoundedCheckIcon size={60} colors={dropzoneColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("transfer.transferProcessedTitle")}
                highlightColor={TITLE_COLOR}
                secudnary={t("transfer.transferProcessedSubtitle")}
                secudnaryColor={TITLE_COLOR}
              />
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={handleDone}
            >
              {t("transfer.done")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
    );
  }

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <CashSpecialIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t(
                isCashFunding
                  ? "transfer.creatingTransfer"
                  : "transfer.transferring",
              )}
              highlightColor={TITLE_COLOR}
              secudnary={formatLabeledAmount(displayAmount, displayCurrency)}
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
